// VOID Conflict — Wingman Clearance Modal
//
// Allows an authenticated Commander to allocate or retrieve exactly 1 one-shot
// Closed Alpha invite clearance key for a friend ("Recruit a Wingman").

import { getAuth, ensureIdentity } from '../net/playerIdentity.js';

let activeOverlay = null;

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export async function showWingmanModal(game) {
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
  }
  game?.overlayGate?.acquire?.();

  const audio = game?.audio;
  audio?.playUiClick?.();

  // Create loading layer
  const layer = document.createElement('div');
  layer.id = 'wingman-modal';
  layer.className = 'fullscreen-dialog-overlay active';
  layer.style.zIndex = '12050';

  layer.innerHTML = `
    <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
      <div class="fullscreen-dialog-bg-image"></div>
      <div class="fullscreen-dialog-scrim"></div>
      <div class="fullscreen-dialog-vignette"></div>
    </div>
    <div class="fullscreen-dialog-container" style="justify-content: center; align-items: center; min-height: 100vh;">
      <div class="fullscreen-card-panel" style="max-width: 520px; width: 92%; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 16px; padding: clamp(24px, 4vh, 36px) clamp(20px, 3.5vw, 32px); border: 1.5px solid rgba(245, 158, 11, 0.45); box-shadow: 0 0 24px rgba(245, 158, 11, 0.25);">
        <div class="fullscreen-dialog-status-pill status-pill-circular" style="border-color: rgba(245, 158, 11, 0.5);">
          <span class="status-indicator-dot" style="background: #fbbf24; box-shadow: 0 0 8px #fbbf24;"></span>
        </div>
        <div style="font-family: 'Orbitron', sans-serif; font-size: clamp(18px, 2.2vw, 22px); font-weight: 900; letter-spacing: 2px; color: #fbbf24; text-shadow: 0 0 16px rgba(245, 158, 11, 0.45); margin: 0;">
          WINGMAN CLEARANCE
        </div>
        <div id="wingman-content" style="width: 100%; display: flex; flex-direction: column; align-items: center; gap: 14px;">
          <div style="font-family: 'Rajdhani', sans-serif; font-size: 16px; color: #94a3b8; font-weight: 600;">
            Allocating operative clearance...
          </div>
        </div>
        <button type="button" class="fullscreen-return-btn" id="wingman-close-btn" style="margin-top: 8px;">
          <span class="start-btn-text">RETURN</span>
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(layer);
  activeOverlay = layer;

  const closeModal = () => {
    audio?.playUiClick?.();
    layer.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
  };

  layer.querySelector('#wingman-close-btn')?.addEventListener('click', closeModal);
  layer.addEventListener('click', (e) => {
    if (e.target === layer) closeModal();
  });

  const contentEl = layer.querySelector('#wingman-content');

  try {
    let auth = getAuth();
    if (!auth || !auth.accountId || !auth.secret) {
      auth = await ensureIdentity();
    }

    if (!auth || !auth.accountId || !auth.secret) {
      throw new Error('Could not establish Commander credentials.');
    }

    const res = await fetch('api/wingman_clearance.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accountId: auth.accountId,
        secret: auth.secret
      })
    });

    const data = await res.json();
    if (!data.ok) {
      throw new Error(data.message || 'Failed to allocate Wingman clearance.');
    }

    const isUsed = !!data.isUsed;
    const code = escapeHtml(data.code);
    const link = escapeHtml(data.url || `https://voidconflict.com/?alpha=${code}`);
    const shareText = data.shareText || `https://voidconflict.com/?alpha=${code}`;
    const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    contentEl.innerHTML = `
      <div style="font-family: 'Rajdhani', sans-serif; font-size: 15px; font-weight: 600; line-height: 1.5; color: #e2e8f0; text-align: center;">
        Recruit 1 trusted Wingman to the Closed Alpha. They receive immediate 1-click clearance and a <strong>VIP Starter Pack</strong> (+500 Fugazi, 5 Supply Tickets).
      </div>

      <div style="width: 100%; background: rgba(0, 0, 0, 0.45); border: 1.5px solid ${isUsed ? 'rgba(239, 68, 68, 0.4)' : 'rgba(0, 240, 255, 0.35)'}; border-radius: 8px; padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; text-align: left;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="font-family: 'Orbitron', sans-serif; font-size: 11px; font-weight: 800; letter-spacing: 1.5px; color: #94a3b8;">
            CLEARANCE KEY
          </span>
          <span style="font-family: 'Rajdhani', sans-serif; font-size: 12px; font-weight: 700; color: ${isUsed ? '#f87171' : '#34d399'}; letter-spacing: 1px;">
            ${isUsed ? '● CLAIMED' : '● ACTIVE // UNUSED'}
          </span>
        </div>
        <div style="font-family: 'IBM Plex Mono', monospace; font-size: 18px; font-weight: 900; letter-spacing: 2.5px; color: ${isUsed ? '#94a3b8' : '#00f0ff'}; text-shadow: ${isUsed ? 'none' : '0 0 10px rgba(0, 240, 255, 0.5)'}; word-break: break-all;">
          ${code}
        </div>
      </div>

      <div style="display: flex; flex-direction: column; gap: 8px; width: 100%; margin-top: 4px;">
        ${canShare && !isUsed ? `
          <button type="button" class="start-btn start-btn-primary" id="btn-share-wingman" style="width: 100%; height: 46px;">
            <span class="start-btn-text" id="lbl-share-wingman">🚀 SHARE CLEARANCE</span>
          </button>
          <button type="button" class="start-btn start-btn-secondary" id="btn-copy-wingman-link" style="width: 100%; height: 42px;">
            <span class="start-btn-text" id="lbl-copy-wingman-link">COPY INVITATION LINK</span>
          </button>
        ` : `
          <button type="button" class="start-btn ${isUsed ? 'start-btn-secondary' : 'start-btn-primary'}" id="btn-copy-wingman-link" style="width: 100%; height: 46px;">
            <span class="start-btn-text" id="lbl-copy-wingman-link">COPY INVITATION LINK</span>
          </button>
          <button type="button" class="start-btn start-btn-secondary" id="btn-copy-wingman-msg" style="width: 100%; height: 42px;">
            <span class="start-btn-text" id="lbl-copy-wingman-msg">COPY FULL CLEARANCE MESSAGE</span>
          </button>
        `}
      </div>
    `;

    // Native Web Share API action
    const shareBtn = contentEl.querySelector('#btn-share-wingman');
    const shareLabel = contentEl.querySelector('#lbl-share-wingman');
    shareBtn?.addEventListener('click', async () => {
      audio?.playUiClick?.();
      try {
        if (navigator.share) {
          await navigator.share({
            title: 'VOID Conflict — Closed Alpha Clearance',
            text: shareText,
            url: link
          });
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('Share API failed, falling back to clipboard:', err);
          try {
            await navigator.clipboard.writeText(link);
            if (shareLabel) {
              const prev = shareLabel.textContent;
              shareLabel.textContent = '✓ LINK COPIED TO CLIPBOARD';
              setTimeout(() => { shareLabel.textContent = prev; }, 2000);
            }
          } catch {}
        }
      }
    });

    // Copy actions
    const copyLinkBtn = contentEl.querySelector('#btn-copy-wingman-link');
    const copyLinkLabel = contentEl.querySelector('#lbl-copy-wingman-link');
    copyLinkBtn?.addEventListener('click', async () => {
      audio?.playUiClick?.();
      try {
        await navigator.clipboard.writeText(link);
        if (copyLinkLabel) {
          const prev = copyLinkLabel.textContent;
          copyLinkLabel.textContent = '✓ LINK COPIED TO CLIPBOARD';
          setTimeout(() => { copyLinkLabel.textContent = prev; }, 2000);
        }
      } catch (err) {
        console.warn('Clipboard write failed:', err);
      }
    });

    const copyMsgBtn = contentEl.querySelector('#btn-copy-wingman-msg');
    const copyMsgLabel = contentEl.querySelector('#lbl-copy-wingman-msg');
    copyMsgBtn?.addEventListener('click', async () => {
      audio?.playUiClick?.();
      try {
        await navigator.clipboard.writeText(shareText);
        if (copyMsgLabel) {
          const prev = copyMsgLabel.textContent;
          copyMsgLabel.textContent = '✓ MESSAGE COPIED TO CLIPBOARD';
          setTimeout(() => { copyMsgLabel.textContent = prev; }, 2000);
        }
      } catch (err) {
        console.warn('Clipboard write failed:', err);
      }
    });
  } catch (err) {
    contentEl.innerHTML = `
      <div style="font-family: 'Rajdhani', sans-serif; font-size: 15px; color: #f87171; font-weight: 700;">
        ${escapeHtml(err.message || 'Clearance allocation unavailable.')}
      </div>
    `;
  }
}
